import QRCode from "qrcode";
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { AppNav } from "../../components/AppNav";
import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { ApiError, api } from "../../lib/api";
import type {
  PartnerStatusResponse,
  PhoneRowResponse,
  TelegramLinkCodeResponse,
  ThemePreference,
  WalletResponse,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { applyThemePref } from "../../lib/theme";
import styles from "./AccountSettings.module.css";
import { PartnerTab } from "./PartnerTab";
import { WalletTab } from "./WalletTab";

/** Display a US number as +1 (xxx) xxx-xxxx while typing; tolerate partial input. */
const formatUsPhone = (raw: string): string => {
  let d = raw.replace(/\D/g, "");
  if (d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** What the API stores: E.164 (+1XXXXXXXXXX), or empty when the field is blank. */
const toE164 = (display: string): string => {
  let d = display.replace(/\D/g, "");
  if (d.startsWith("1")) d = d.slice(1);
  return d.length === 0 ? "" : `+1${d.slice(0, 10)}`;
};

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

type TabKey = "notifications" | "profile" | "security" | "wallet" | "partner";
const BASE_TABS: readonly TabKey[] = ["notifications", "profile", "security"];
const TAB_LABELS: Record<TabKey, string> = {
  notifications: "Notifications",
  profile: "Profile",
  security: "Security",
  wallet: "Wallet",
  partner: "Partner",
};

/** One unambiguous state per number; the row UI hangs off this. */
const phoneBadge = (p: PhoneRowResponse): { label: string; variant: string } => {
  if (p.carrier_blocked_at) return { label: "Unsubscribed (STOP)", variant: "stop" };
  if (!p.verified_at) return { label: "Verify pending", variant: "pending" };
  if (p.sms_consent_at) return { label: "Active", variant: "active" };
  return { label: "Alerts off", variant: "off" };
};

// Center-crop to a square and shrink to a small JPEG data URL, so the avatar stays tiny
// enough to live inline in the database (no object store) and matches the backend validator.
const cropToSquareDataUrl = (file: File, size = 256): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas unavailable"));
        return;
      }
      ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not read image"));
    };
    img.src = url;
  });

// 8-char codes read/type easier split in half: K7M2P9WX -> K7M2-P9WX.
const formatLinkCode = (code: string): string =>
  code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;

const TelegramCeremonyDiagram = () => (
  <svg
    role="img"
    aria-label="How Telegram linking works: 1 generate a code here, 2 open @videorag_bot in Telegram, 3 send it the code - alerts then arrive in that chat"
    viewBox="0 0 640 150"
    width="100%"
    style={{ maxWidth: 640 }}
  >
    <title>How Telegram linking works</title>
    <defs>
      <marker
        id="tg-arrow"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="7"
        markerHeight="7"
        orient="auto-start-reverse"
      >
        <path d="M0 0 L10 5 L0 10 z" fill="var(--color-text-faint)" />
      </marker>
    </defs>
    {[
      {
        x: 10,
        n: "1",
        title: "Generate a code",
        sub: "on this page",
        extra: "e.g. kX92…",
        accent: "#7c6fe8",
      },
      {
        x: 230,
        n: "2",
        title: "Open the bot",
        sub: "@videorag_bot",
        extra: "tap Start",
        accent: "#2aa1da",
      },
      {
        x: 450,
        n: "3",
        title: "Send the code",
        sub: "in that chat",
        extra: "bot replies: linked \u2713",
        accent: "#4caf7d",
      },
    ].map((step) => (
      <g key={step.n}>
        <rect
          x={step.x}
          y={20}
          width={180}
          height={100}
          rx={12}
          fill="var(--color-surface-2)"
          stroke="var(--color-border)"
        />
        <circle cx={step.x + 26} cy={46} r={13} fill={step.accent} />
        <text x={step.x + 26} y={51} textAnchor="middle" fontSize="14" fontWeight="700" fill="#fff">
          {step.n}
        </text>
        <text x={step.x + 48} y={51} fontSize="14" fontWeight="600" fill="var(--color-text-strong)">
          {step.title}
        </text>
        <text x={step.x + 16} y={78} fontSize="13" fill="var(--color-text-muted)">
          {step.sub}
        </text>
        <text x={step.x + 16} y={102} fontSize="12" fill="var(--color-text-faint)">
          {step.extra}
        </text>
      </g>
    ))}
    <line
      x1={192}
      y1={70}
      x2={226}
      y2={70}
      stroke="var(--color-text-faint)"
      strokeWidth={2}
      markerEnd="url(#tg-arrow)"
    />
    <line
      x1={412}
      y1={70}
      x2={446}
      y2={70}
      stroke="var(--color-text-faint)"
      strokeWidth={2}
      markerEnd="url(#tg-arrow)"
    />
    <text x={320} y={143} textAnchor="middle" fontSize="12" fill="var(--color-text-muted)">
      One time only - afterwards alerts and answers arrive in that private chat.
    </text>
  </svg>
);

export const AccountSettings = () => {
  const { user, setUser, logout } = useAuth();
  const [searchParams] = useSearchParams();
  // The conditional-tab pattern for account programmes: the Partner tab exists iff
  // the user has a sales_partners row (PRD PAYWALL §1.1; GET /api/partner 404s
  // otherwise), and its sibling Wallet tab (§6, item 7) exists iff GET /api/wallet
  // answers — an affiliates row or any Account Wallet ledger history.
  const [partner, setPartner] = useState<PartnerStatusResponse | null>(null);
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const tabs: readonly TabKey[] = [
    ...BASE_TABS,
    ...(wallet ? (["wallet"] as const) : []),
    ...(partner ? (["partner"] as const) : []),
  ];
  const tabParam = searchParams.get("tab") as TabKey | null;
  const activeTab: TabKey = tabParam && tabs.includes(tabParam) ? tabParam : "notifications";

  const [name, setName] = useState(user?.display_name ?? "");
  const [avatar, setAvatar] = useState<string | null>(user?.avatar ?? null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Sign-in & security: change password, and the two-step email change (the OTP goes
  // to the NEW address; the login identity only swaps once the code verifies).
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [emailNew, setEmailNew] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleChangePassword = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setPwBusy(true);
    setPwDone(false);
    setPwError(null);
    try {
      await api.changePassword({ current_password: pwCurrent, new_password: pwNew });
      setPwCurrent("");
      setPwNew("");
      setPwDone(true);
    } catch (e) {
      setPwError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't change the password.",
      );
    } finally {
      setPwBusy(false);
    }
  };

  const handleEmailStart = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setEmailBusy(true);
    setEmailDone(false);
    setEmailError(null);
    try {
      await api.startEmailChange({ new_email: emailNew.trim(), password: emailPassword });
      setEmailCodeSent(true);
    } catch (e) {
      setEmailError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't send the code.",
      );
    } finally {
      setEmailBusy(false);
    }
  };

  const handleEmailCheck = async (): Promise<void> => {
    setEmailBusy(true);
    setEmailError(null);
    try {
      await api.checkEmailChange({
        new_email: emailNew.trim(),
        code: emailCode.trim(),
        password: emailPassword,
      });
      setEmailDone(true);
      setEmailCodeSent(false);
      setEmailNew("");
      setEmailPassword("");
      setEmailCode("");
    } catch (e) {
      setEmailError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't verify the code.",
      );
    } finally {
      setEmailBusy(false);
    }
  };
  const fileRef = useRef<HTMLInputElement>(null);

  // Notification phones (cap 5). Verification, consent and the carrier STOP flag are
  // per NUMBER; alerts go to every verified + consented number.
  const MAX_PHONES = 5;
  const [phones, setPhones] = useState<PhoneRowResponse[]>([]);
  const [newPhone, setNewPhone] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [verifyingPhone, setVerifyingPhone] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState("");

  const [mediaConsent, setMediaConsent] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [savingMedia, setSavingMedia] = useState(false);

  const [linking, setLinking] = useState(false);
  const [link, setLink] = useState<TelegramLinkCodeResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleAvatarFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setProfileError(null);
    setProfileSaved(false);
    try {
      setAvatar(await cropToSquareDataUrl(file));
    } catch {
      setProfileError("Couldn't read that image. Try a PNG or JPEG.");
    }
  };

  const handleTheme = async (pref: ThemePreference): Promise<void> => {
    setThemeError(null);
    setSavingTheme(true);
    applyThemePref(pref); // paint immediately; the PATCH persists it account-wide
    try {
      const updated = await api.updateMe({ theme: pref });
      setUser(updated);
    } catch (e) {
      setThemeError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't save the theme.",
      );
      applyThemePref(user?.theme ?? "system"); // roll the paint back to the saved choice
    } finally {
      setSavingTheme(false);
    }
  };

  const handleSaveProfile = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    const trimmed = name.trim();
    try {
      const updated = await api.updateMe({
        display_name: trimmed === "" ? null : trimmed,
        avatar,
      });
      setUser(updated);
      setProfileSaved(true);
    } catch (e) {
      setProfileError(errorText(e, "Couldn't save your profile."));
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await api.listPhones();
        if (!cancelled) setPhones(rows);
      } catch {
        // An empty list here is a LIE (it hid the 2026-08-13 grant outage) — say so.
        if (!cancelled)
          setPhoneError("Couldn't load your notification numbers. Reload to try again.");
      }
      try {
        const consent = await api.getMediaConsent();
        if (!cancelled) setMediaConsent(consent.media_consent_at !== null);
      } catch {
        // checkbox stays off; toggling still works
      }
      try {
        const row = await api.getPartner();
        if (!cancelled) setPartner(row);
      } catch {
        // 404 = not a partner — the tab simply doesn't render
      }
      try {
        const w = await api.getWallet();
        if (!cancelled) setWallet(w);
      } catch {
        // 404 = no affiliates row and no ledger history — the tab simply doesn't render
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadPhones = async (): Promise<void> => {
    try {
      setPhones(await api.listPhones());
    } catch {
      // keep the current list; the next mutation retries
    }
  };

  const handleAddPhone = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const e164 = toE164(newPhone);
    if (!e164) return;
    setPhoneBusy(true);
    setPhoneError(null);
    try {
      await api.addPhone(e164);
      setNewPhone("");
      setVerifyingPhone(e164);
      setVerifyCode("");
      await reloadPhones();
    } catch (e) {
      setPhoneError(errorText(e, "Couldn't add that number."));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleSendRowCode = async (rowPhone: string): Promise<void> => {
    setPhoneError(null);
    try {
      await api.resendPhoneCode(rowPhone);
      setVerifyingPhone(rowPhone);
      setVerifyCode("");
    } catch (e) {
      setPhoneError(errorText(e, "Couldn't send the code."));
    }
  };

  const handleConfirmRowCode = async (rowPhone: string): Promise<void> => {
    setPhoneBusy(true);
    setPhoneError(null);
    try {
      await api.verifyPhoneRow(rowPhone, verifyCode.trim());
      setVerifyingPhone(null);
      setVerifyCode("");
      await reloadPhones();
    } catch (e) {
      setPhoneError(errorText(e, "Couldn't verify the code."));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleRemovePhone = async (rowPhone: string): Promise<void> => {
    const eligible = phones.filter(
      (r) => r.verified_at && r.sms_consent_at && !r.carrier_blocked_at,
    );
    if (
      eligible.length === 1 &&
      eligible[0]?.phone === rowPhone &&
      !window.confirm(
        "This is the last number receiving SMS alerts — removing it stops SMS alerts for your account. Remove anyway?",
      )
    ) {
      return;
    }
    setPhoneError(null);
    try {
      await api.removePhone(rowPhone);
      if (verifyingPhone === rowPhone) setVerifyingPhone(null);
      await reloadPhones();
    } catch (e) {
      setPhoneError(errorText(e, "Couldn't remove that number."));
    }
  };

  const handleRowConsent = async (rowPhone: string, consent: boolean): Promise<void> => {
    setPhoneError(null);
    try {
      await api.setPhoneRowConsent(rowPhone, consent);
      await reloadPhones();
    } catch (e) {
      setPhoneError(errorText(e, "Couldn't change the SMS consent."));
    }
  };

  const handleMediaConsent = async (next: boolean): Promise<void> => {
    setSavingMedia(true);
    setMediaError(null);
    try {
      const saved = await api.setMediaConsent(next);
      setMediaConsent(saved.media_consent_at !== null);
    } catch (e) {
      setMediaError(errorText(e, "Couldn't save your media preference."));
    } finally {
      setSavingMedia(false);
    }
  };

  const handleLinkTelegram = async (): Promise<void> => {
    setLinking(true);
    setLinkError(null);
    try {
      const minted = await api.createTelegramLinkCode();
      setLink(minted);
      setQrDataUrl(await QRCode.toDataURL(minted.deep_link, { margin: 1, width: 360 }));
    } catch (e) {
      setLinkError(errorText(e, "Couldn't create a link code."));
    } finally {
      setLinking(false);
    }
  };

  return (
    <Layout
      nav={
        <AppNav
          trail={[{ label: "Account" }]}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main className={styles.main}>
        <header className={styles.head}>
          <h1 className={styles.title}>Account</h1>
          <p className={styles.subtitle}>
            How the agents reach you when something needs your attention.
          </p>
        </header>

        <nav aria-label="Account sections" className={styles.tabbar}>
          {tabs.map((key) => (
            <Link
              key={key}
              to={`?tab=${key}`}
              className={styles.tab}
              data-active={key === activeTab}
            >
              {TAB_LABELS[key]}
            </Link>
          ))}
        </nav>

        {activeTab === "wallet" && wallet && <WalletTab wallet={wallet} onChanged={setWallet} />}

        {activeTab === "partner" && partner && (
          <PartnerTab partner={partner} onChanged={setPartner} />
        )}

        {activeTab === "profile" && (
          <>
            <section className={styles.panel}>
              <span className={styles.kicker}>Profile</span>
              <form className={styles.form} onSubmit={handleSaveProfile} noValidate>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <Avatar
                    name={name || user?.email || "Account"}
                    src={avatar ?? undefined}
                    size="lg"
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: "none" }}
                    onChange={(e) => void handleAvatarFile(e)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={savingProfile}
                  >
                    Upload photo
                  </Button>
                  {avatar && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setAvatar(null);
                        setProfileSaved(false);
                      }}
                      disabled={savingProfile}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="account-name">
                    Full name
                  </label>
                  <Input
                    id="account-name"
                    value={name}
                    placeholder="Your name"
                    onChange={(e) => {
                      setName(e.target.value);
                      setProfileSaved(false);
                    }}
                    disabled={savingProfile}
                  />
                </div>
                <div className={styles.actions}>
                  <Button type="submit" disabled={savingProfile}>
                    {savingProfile ? "Saving…" : "Save profile"}
                  </Button>
                  {profileSaved && <span className={styles.ok}>Saved.</span>}
                </div>
                {profileError && (
                  <div role="alert" className={styles.error}>
                    {profileError}
                  </div>
                )}
              </form>
            </section>
            <section className={styles.panel} aria-label="Appearance">
              <span className={styles.kicker}>Appearance</span>
              <p className={styles.hint}>
                Choose how Shobdo looks on your account. System follows your device's light/dark
                setting.
              </p>
              <div role="radiogroup" aria-label="Theme" style={{ display: "flex", gap: "8px" }}>
                {(["light", "dark", "system"] as const).map((pref) => (
                  <Button
                    key={pref}
                    type="button"
                    variant={(user?.theme ?? "system") === pref ? "primary" : "outline"}
                    aria-pressed={(user?.theme ?? "system") === pref}
                    onClick={() => void handleTheme(pref)}
                    disabled={savingTheme}
                  >
                    {pref === "light" ? "Light" : pref === "dark" ? "Dark" : "System"}
                  </Button>
                ))}
                {themeError && (
                  <span role="alert" className={styles.error}>
                    {themeError}
                  </span>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === "security" && (
          <section className={styles.panel}>
            <span className={styles.kicker}>Sign-in &amp; security</span>
            {user?.has_password === false ? (
              // A Google-only account has no password to re-prove (ADR 0007).
              <p className={styles.hint}>
                You sign in with Google. A password is needed to change your email, transfer a store
                or play recorded clips: set one with{" "}
                <Link to="/recover">Forgot your password?</Link> — it emails you a code.
              </p>
            ) : (
              <>
                <form className={styles.form} onSubmit={handleChangePassword} noValidate>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="pw-current">
                      Current password
                    </label>
                    <Input
                      id="pw-current"
                      type="password"
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                      disabled={pwBusy}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="pw-new">
                      New password
                    </label>
                    <Input
                      id="pw-new"
                      type="password"
                      value={pwNew}
                      onChange={(e) => setPwNew(e.target.value)}
                      disabled={pwBusy}
                      required
                    />
                    <span className={styles.help}>
                      At least 8 characters, combining three of: lowercase, uppercase, digits,
                      symbols.
                    </span>
                  </div>
                  <div className={styles.actions}>
                    <Button type="submit" disabled={pwBusy || !pwCurrent || !pwNew}>
                      {pwBusy ? "Changing…" : "Change password"}
                    </Button>
                    {pwDone && (
                      <span className={styles.ok}>
                        Password updated — you'll be asked to sign in again.
                      </span>
                    )}
                  </div>
                  {pwError && (
                    <div role="alert" className={styles.error}>
                      {pwError}
                    </div>
                  )}
                </form>

                <form className={styles.form} onSubmit={handleEmailStart} noValidate>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="email-new">
                      New email
                    </label>
                    <Input
                      id="email-new"
                      type="email"
                      placeholder={user?.email ?? "you@example.com"}
                      value={emailNew}
                      onChange={(e) => {
                        setEmailNew(e.target.value);
                        setEmailCodeSent(false);
                        setEmailDone(false);
                      }}
                      disabled={emailBusy}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="email-password">
                      Confirm with password
                    </label>
                    <Input
                      id="email-password"
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      disabled={emailBusy}
                      required
                    />
                  </div>
                  <div className={styles.actions}>
                    <Button type="submit" disabled={emailBusy || !emailNew || !emailPassword}>
                      {emailBusy && !emailCodeSent ? "Sending…" : "Verify new email"}
                    </Button>
                    {emailDone && <span className={styles.ok}>Email updated.</span>}
                  </div>
                  {emailCodeSent && (
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="email-code">
                        Email verification code
                      </label>
                      <Input
                        id="email-code"
                        inputMode="numeric"
                        placeholder="Code sent to the new address"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (emailCode.trim()) void handleEmailCheck();
                          }
                        }}
                        disabled={emailBusy}
                      />
                      <div className={styles.actions}>
                        <Button
                          type="button"
                          disabled={emailBusy || !emailCode}
                          onClick={() => void handleEmailCheck()}
                        >
                          Confirm new email
                        </Button>
                      </div>
                    </div>
                  )}
                  {emailError && (
                    <div role="alert" className={styles.error}>
                      {emailError}
                    </div>
                  )}
                </form>
              </>
            )}
          </section>
        )}

        {activeTab === "notifications" && (
          <>
            <section className={styles.panel}>
              <span className={styles.kicker}>
                Notification contacts
                <span className={styles.counter}>{`${phones.length} of ${MAX_PHONES}`}</span>
              </span>
              <div className={styles.form}>
                <span className={styles.help}>
                  Alerts are texted to every verified number below that has SMS alerts on (up to{" "}
                  {MAX_PHONES} numbers).
                </span>
                <ul className={styles.phoneList} aria-label="Notification phone numbers">
                  {phones.map((p) => {
                    const badge = phoneBadge(p);
                    return (
                      <li key={p.phone} className={styles.phoneRow}>
                        <span className={styles.phoneNumber}>{formatUsPhone(p.phone)}</span>
                        <span className={styles.badge} data-variant={badge.variant}>
                          {badge.label}
                        </span>
                        {p.carrier_blocked_at ? (
                          <span className={styles.stopWarning}>
                            Unsubscribed by text (STOP) on{" "}
                            {new Date(p.carrier_blocked_at).toLocaleDateString()}
                            {" — alerts paused. Text START to (234) 444-4912 to resume."}
                          </span>
                        ) : p.verified_at ? (
                          <label className={styles.consentRow}>
                            <input
                              type="checkbox"
                              aria-label={`SMS alerts to ${formatUsPhone(p.phone)}`}
                              checked={p.sms_consent_at !== null}
                              onChange={(e) => void handleRowConsent(p.phone, e.target.checked)}
                              disabled={phoneBusy}
                            />
                            <span>SMS alerts</span>
                          </label>
                        ) : (
                          <>
                            {verifyingPhone === p.phone ? (
                              <>
                                <Input
                                  aria-label={`Verification code for ${formatUsPhone(p.phone)}`}
                                  inputMode="numeric"
                                  placeholder="123456"
                                  value={verifyCode}
                                  onChange={(e) => setVerifyCode(e.target.value)}
                                  disabled={phoneBusy}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={phoneBusy || verifyCode.trim().length === 0}
                                  onClick={() => void handleConfirmRowCode(p.phone)}
                                >
                                  Confirm
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => void handleSendRowCode(p.phone)}
                              >
                                Send code
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          aria-label={`Remove ${formatUsPhone(p.phone)}`}
                          onClick={() => void handleRemovePhone(p.phone)}
                        >
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
                {phones.length < MAX_PHONES && (
                  <form className={styles.actions} onSubmit={handleAddPhone} noValidate>
                    <Input
                      aria-label="Add phone number"
                      type="tel"
                      className={styles.phoneInput}
                      placeholder="+1 (000) 000-0000"
                      value={newPhone}
                      onChange={(e) => setNewPhone(formatUsPhone(e.target.value))}
                      disabled={phoneBusy}
                    />
                    <Button type="submit" disabled={phoneBusy || toE164(newPhone) === ""}>
                      Add number
                    </Button>
                  </form>
                )}
                <span className={styles.help}>
                  To change a number, add the new one first, verify it, then remove the old one.
                </span>
                <span className={styles.consentText}>
                  Ticking "SMS alerts" on a number means: I agree to receive security alert text
                  messages from Shobdo at the numbers I select. Message frequency varies with
                  activity at your stores. Message & data rates may apply. Reply HELP for help, STOP
                  to unsubscribe at any time. See our{" "}
                  <a href="https://www.shobdo.ai/privacy.html" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="https://www.shobdo.ai/terms.html" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>
                  . SMS alerts are optional — every other feature works without them.
                </span>
                {phoneError && (
                  <div role="alert" className={styles.error}>
                    {phoneError}
                  </div>
                )}
              </div>
            </section>

            <section className={styles.panel}>
              <span className={styles.kicker}>Alert media</span>
              <label className={styles.consentRow} htmlFor="media-consent">
                <input
                  id="media-consent"
                  type="checkbox"
                  checked={mediaConsent}
                  onChange={(e) => void handleMediaConsent(e.target.checked)}
                  disabled={savingMedia}
                />
                <span className={styles.consentText}>
                  Include a short video clip (GIF) of the detected activity with my alert messages,
                  on every channel. Without this, alerts still arrive as text with a link. One
                  setting for your whole account.
                </span>
              </label>
              {mediaError && (
                <div role="alert" className={styles.error}>
                  {mediaError}
                </div>
              )}
            </section>

            <section className={styles.panel}>
              <span className={styles.kicker}>Telegram</span>
              <p className={styles.help}>
                Link Telegram to chat with the Query agent and receive alerts. The bot is{" "}
                <a href="https://t.me/videorag_bot" target="_blank" rel="noreferrer">
                  @videorag_bot
                </a>
                .
              </p>
              <TelegramCeremonyDiagram />
              <div className={styles.actions}>
                <Button
                  variant="outline"
                  onClick={() => void handleLinkTelegram()}
                  disabled={linking}
                >
                  {linking ? "Generating…" : "Link Telegram"}
                </Button>
              </div>

              {link && (
                <div className={styles.linkBox}>
                  {qrDataUrl && (
                    <img
                      src={qrDataUrl}
                      width={180}
                      height={180}
                      alt="Scan with your phone camera to open Telegram and finish linking"
                    />
                  )}
                  <a
                    className={styles.deepLink}
                    href={link.deep_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    On your phone? Open Telegram directly →
                  </a>
                  <div className={styles.codeRow}>
                    <span className={styles.codeLabel}>Or type this code to the bot</span>
                    <code className={styles.code}>{formatLinkCode(link.code)}</code>
                  </div>
                </div>
              )}

              {linkError && (
                <div role="alert" className={styles.error}>
                  {linkError}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </Layout>
  );
};
